import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Input, Radio, RadioGroup, SelectItem, TmdxSelect } from '@ds';
import { FormSection } from '@features/components/CaseFormBlocks/components';
import { calculateBmi } from '@features/forms/DonorProfileForm';
import { CauseOfDeath, DonorGender } from '@/api/apis/case';
import { DonorProfileFormValues } from '../types';
import { DonorFormRow } from './DonorFormRow';
import { HeightInput } from './HeightInput';
import { WeightInput } from './WeightInput';

const CAUSE_OF_DEATH_OPTIONS: { value: CauseOfDeath; label: string }[] = [
  { value: CauseOfDeath.Anoxia, label: 'Anoxia' },
  { value: CauseOfDeath.Cerebrovascular, label: 'Cerebrovascular/Stroke' },
  { value: CauseOfDeath.HeadTrauma, label: 'Head Trauma' },
  { value: CauseOfDeath.CNSTumor, label: 'CNS Tumor' },
  { value: CauseOfDeath.Other, label: 'Other' },
];

const GENDER_OPTIONS: { value: DonorGender; label: string }[] = [
  { value: DonorGender.Male, label: 'Male' },
  { value: DonorGender.Female, label: 'Female' },
  { value: DonorGender.Other, label: 'Other' },
];

const BmiField = () => {
  const { control, setValue } = useFormContext<DonorProfileFormValues>();
  const [weight, height] = useWatch({ control, name: ['donorWeight', 'donorHeight'] });

  const bmi = calculateBmi(weight, height);

  if (bmi !== null) {
    setValue('donorBmi', bmi);
  }

  return (
    <DonorFormRow title="BMI">
      <div className="w-[332px]">
        <Input value={bmi ?? ''} placeholder="Calculated automatically" aria-label="BMI" isReadOnly isDisabled />
      </div>
    </DonorFormRow>
  );
};

type DonorDemographicsSectionProps = {
  showRequired?: boolean;
};

export const DonorDemographicsSection = ({ showRequired = true }: DonorDemographicsSectionProps) => {
  const { control } = useFormContext<DonorProfileFormValues>();

  return (
    <FormSection title="Donor Demographics">
      <DonorFormRow title="Age" isRequired={showRequired}>
        <div className="w-[332px]">
          <Controller
            name="donorAge"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="1"
                aria-label="Donor age"
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="Weight" isRequired={showRequired}>
        <WeightInput />
      </DonorFormRow>

      <DonorFormRow title="Height" isRequired={showRequired}>
        <HeightInput />
      </DonorFormRow>

      <BmiField />

      <DonorFormRow title="Gender" isRequired={showRequired}>
        <Controller
          name="donorGender"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value ?? ''}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            >
              {GENDER_OPTIONS.map((opt) => (
                <Radio
                  key={opt.value}
                  className="w-[100px] max-w-none"
                  classNames={{ label: 'pl-1' }}
                  value={opt.value}
                  size="lg"
                >
                  {opt.label}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
      </DonorFormRow>

      <DonorFormRow title="COD">
        <div className="w-[332px]">
          <Controller
            name="causeOfDeath"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <TmdxSelect
                placeholder="Select"
                aria-label="Cause of death"
                selectedKeys={value ? [value] : undefined}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
              >
                {CAUSE_OF_DEATH_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value}>{opt.label}</SelectItem>
                ))}
              </TmdxSelect>
            )}
          />
        </div>
      </DonorFormRow>
    </FormSection>
  );
};
